<script lang="ts">
import { BrutoCpmStats } from "@/modules/base_price/utils/brutoCpm";
import ResolvedBrutoCpmDTO = Modules.BasePrice.Data.Resolved.ResolvedBrutoCpmDTO;

/** One row of the bruto tree — a hierarchy node, or a screen hanging off one. */
export type BrutoTreeRow = {
	key: string;
	nodeId: number;
	/** Set for a screen row; the screen's node is `nodeId`. */
	assetId: number | null;
	name: string;
	cpm: ResolvedBrutoCpmDTO;
	/**
	 * Avg / min / max over the screens beneath a level (or a screen's own
	 * months) — see subtreeCpmStats(). Null when there is no CPM at all.
	 */
	stats: BrutoCpmStats | null;
	children: BrutoTreeRow[];
};
</script>

<script setup lang="ts">
import { computed, ref } from "vue";
import { formatNumber } from "chartjs-graph";
import { useBasePriceBrutoStore } from "@/modules/base_price/brutoStore";

const props = defineProps<{
	row: BrutoTreeRow;
	depth: number;
}>();

const store = useBasePriceBrutoStore();

// The top two levels start open, deeper ones closed — a large estate would
// otherwise open as a wall of screens.
const expanded = ref(props.depth < 2);

const isScreen = computed(() => props.row.assetId !== null);

const isSelected = computed(() =>
	isScreen.value
		? store.selectedAssetId === props.row.assetId
		: store.selectedAssetId === null &&
		  store.selectedNodeId === props.row.nodeId
);

const select = () => {
	if (props.row.assetId !== null) {
		store.selectAsset(props.row.nodeId, props.row.assetId);

		return;
	}

	store.selectNode(props.row.nodeId);
};

const money = (value: number | undefined): string =>
	value === undefined ? "—" : formatNumber(value, "money");

const ownMonths = computed(
	() => props.row.cpm.monthly_cpm_own.filter(Boolean).length
);
</script>

<template>
	<div>
		<div
			class="tree-row"
			:class="{
				'tree-row--selected': isSelected,
				'tree-row--own': ownMonths > 0,
			}"
			@click="select"
		>
			<div class="tree-label" :style="{ paddingLeft: `${depth * 18 + 12}px` }">
				<!--
					One chevron rotated by a class rather than two icons swapped by a
					binding — Font Awesome replaces the element with an <svg> after
					insertion, so a swapped icon would never update. See
					PlanHierarchyRow.vue.
				-->
				<span
					class="tree-caret"
					:class="{ 'tree-caret--expanded': expanded }"
					@click.stop="expanded = !expanded"
				>
					<i v-if="row.children.length" class="fa-regular fa-chevron-right" />
					<i
						v-else-if="isScreen"
						class="fa-regular fa-display tree-screen-icon"
					/>
				</span>
				<span
					class="tree-name text-truncate"
					:class="{ 'font-weight-bold': isSelected }"
				>
					{{ row.name }}
				</span>
			</div>

			<span class="tree-col-value">{{ money(row.stats?.avg) }}</span>
			<span class="tree-col-value">{{ money(row.stats?.min) }}</span>
			<span class="tree-col-value">{{ money(row.stats?.max) }}</span>
			<span
				class="tree-col-value"
				:class="{ 'text-medium-emphasis': ownMonths === 0 }"
			>
				<!-- In a span: Font Awesome replaces the <i>, see the caret above. -->
				<span v-if="ownMonths > 0" class="tree-lock-icon">
					<i class="fa-solid fa-lock" />
				</span>
				{{ ownMonths }}/12
			</span>
		</div>

		<template v-if="expanded">
			<bruto-hierarchy-row
				v-for="child in row.children"
				:key="child.key"
				:row="child"
				:depth="depth + 1"
			/>
		</template>
	</div>
</template>

<style scoped lang="scss">
.tree-row {
	display: flex;
	align-items: center;
	gap: 8px;
	padding-top: 6px;
	padding-bottom: 6px;
	padding-right: 12px;
	border-left: 3px solid transparent;
	cursor: pointer;
}

.tree-row:hover {
	background: rgba(var(--v-theme-on-surface), 0.04);
}

.tree-row--selected {
	background: rgba(var(--v-theme-on-surface), 0.06);
}

// Same mark the netto tree uses for "override here".
.tree-row--own {
	border-left-color: rgb(var(--v-theme-warning));
}

.tree-label {
	flex: 1 1 auto;
	min-width: 0;
	max-width: 310px;
	display: flex;
	align-items: center;
	gap: 8px;
	overflow: hidden;
	white-space: nowrap;
}

.tree-caret {
	width: 14px;
	flex: 0 0 auto;
	font-size: 10px;
	text-align: center;
	color: rgba(var(--v-theme-on-surface), 0.6);
}

.tree-caret > * {
	display: inline-block;
	transition: transform 0.15s ease;
}

.tree-caret--expanded > * {
	transform: rotate(90deg);
}

.tree-caret--expanded > .tree-screen-icon {
	transform: none;
}

.tree-screen-icon {
	font-size: 10px;
	color: rgba(var(--v-theme-on-surface), 0.4);
}

.tree-name {
	min-width: 0;
	font-size: 13px;
}

.tree-col-value {
	flex: 0 0 56px;
	text-align: right;
	font-size: 12.5px;
	font-variant-numeric: tabular-nums;
}

.tree-lock-icon {
	font-size: 9px;
	margin-right: 2px;
	color: rgb(var(--v-theme-warning));
}
</style>
